import { useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { X, Wrench, Zap, Activity } from 'lucide-react';
import { ModalRoot } from '@/components/ui/ModalRoot';
import { Z_MODALS } from '@/lib/zIndex';
import { useGameStore } from '@/state/gameStore';
import { VEHICLE_SPECS } from '@/data/vehicles';
import {
  baseRepairCost,
  repairCostFor,
  repairDurationMs,
  REPAIR_DISABLE_THRESHOLD,
} from '@/data/repair';
import { formatEuro } from '@/lib/format';
import type { Vehicle } from '@/state/types';

interface WorkshopDialogProps {
  vehicle: Vehicle | null;
  onClose: () => void;
}

function formatMinutes(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec} s`;
  return `${Math.round(sec / 60)} min`;
}

export function WorkshopDialog({ vehicle, onClose }: WorkshopDialogProps) {
  const startRepair = useGameStore((s) => s.startRepair);
  const credits = useGameStore((s) => s.credits);
  const mode = useGameStore((s) => s.mode);
  const pushToast = useGameStore((s) => s.pushToast);

  useEffect(() => {
    if (!vehicle) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [vehicle, onClose]);

  if (!vehicle) return null;

  const spec = VEHICLE_SPECS[vehicle.type];
  const conditionPct = Math.round(vehicle.condition * 100);
  const isCritical = vehicle.condition <= REPAIR_DISABLE_THRESHOLD;
  const base = baseRepairCost(vehicle);
  const standardCost = repairCostFor(vehicle, 'standard');
  const instantCost = repairCostFor(vehicle, 'instant');
  const standardCanAfford = mode === 'sandbox' || credits >= standardCost;
  const instantCanAfford = mode === 'sandbox' || credits >= instantCost;
  const inRepair = vehicle.repairStartedAt !== undefined;
  const notAtBase = vehicle.status !== 2;

  const handle = (kind: 'standard' | 'instant') => {
    const ok = startRepair(vehicle.id, kind);
    if (ok) {
      pushToast({
        kind: 'info',
        text: `${spec.label}: Reparatur gestartet (${formatMinutes(repairDurationMs(kind))}).`,
      });
      onClose();
    } else {
      pushToast({
        kind: 'error',
        text: notAtBase
          ? `${spec.label}: Fahrzeug muss an der Wache sein.`
          : `${spec.label}: Reparatur fehlgeschlagen.`,
      });
    }
  };

  return (
    <AnimatePresence>
      <ModalRoot
        onClose={onClose}
        zIndex={Z_MODALS}
        contentClass="w-full max-w-lg rounded-lg border border-border-strong bg-bg-secondary p-6"
      >
          <div className="mb-4 flex items-start justify-between">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-amber-500/15 text-amber-400">
                <Wrench className="h-4 w-4" aria-hidden />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-amber-400">Werkstatt</p>
                <h3 id="workshop-title" className="font-display text-lg font-semibold">
                  {spec.label}
                </h3>
                <p className="mt-0.5 text-xs text-fg-muted">{spec.fullName}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="text-fg-muted transition hover:text-fg-primary"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          {/* Condition bar */}
          <div className="rounded-md border border-border-subtle bg-bg-elevated/40 p-4">
            <div className="flex items-center gap-3">
              <Activity
                className={[
                  'h-4 w-4',
                  isCritical ? 'text-accent-emergency' : 'text-amber-400',
                ].join(' ')}
                aria-hidden
              />
              <p className="flex-1 text-xs uppercase tracking-wider text-fg-muted">Kondition</p>
              <p
                className={[
                  'font-display text-xl font-bold tabular-nums',
                  isCritical ? 'text-accent-emergency' : 'text-fg-primary',
                ].join(' ')}
              >
                {conditionPct} %
              </p>
            </div>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-bg-primary">
              <div
                className={[
                  'h-full rounded-full transition-all',
                  isCritical ? 'bg-accent-emergency' : 'bg-amber-400',
                ].join(' ')}
                style={{ width: `${conditionPct}%` }}
              />
            </div>
            {base === 0 && (
              <p className="mt-2 text-xs text-status-available">
                Fahrzeug ist in Bestzustand — keine Reparatur nötig.
              </p>
            )}
          </div>

          {inRepair ? (
            <p className="mt-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              Reparatur läuft bereits. Schließe diesen Dialog und warte den Status-Wechsel ab.
            </p>
          ) : notAtBase ? (
            <p className="mt-4 rounded-md border border-accent-emergency/40 bg-accent-emergency/10 px-3 py-2 text-xs text-accent-emergency">
              Reparatur nur an der Wache möglich. Fahrzeug zuerst zurückrufen.
            </p>
          ) : (
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <RepairOption
                title="Standard"
                duration="ca. 5 min"
                cost={standardCost}
                disabled={base === 0 || !standardCanAfford}
                onClick={() => handle('standard')}
                primary={false}
                description="Günstige Werkstatt, längere Wartezeit."
                icon={Wrench}
              />
              <RepairOption
                title="Sofort"
                duration="ca. 30 s"
                cost={instantCost}
                disabled={base === 0 || !instantCanAfford}
                onClick={() => handle('instant')}
                primary
                description="Premium-Service, sofort verfügbar."
                icon={Zap}
              />
            </div>
          )}
      </ModalRoot>
    </AnimatePresence>
  );
}

interface RepairOptionProps {
  title: string;
  duration: string;
  cost: number;
  disabled: boolean;
  onClick: () => void;
  primary: boolean;
  description: string;
  icon: typeof Wrench;
}

function RepairOption({
  title,
  duration,
  cost,
  disabled,
  onClick,
  primary,
  description,
  icon: Icon,
}: RepairOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'flex h-full flex-col items-start gap-2 rounded-md border p-4 text-left transition',
        disabled
          ? 'cursor-not-allowed border-border-subtle bg-bg-elevated/20 opacity-50'
          : primary
            ? 'border-accent-dispatch/60 bg-accent-dispatch/10 hover:border-accent-dispatch hover:bg-accent-dispatch/15'
            : 'border-border-subtle bg-bg-elevated/40 hover:border-border-strong hover:bg-bg-elevated',
      ].join(' ')}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" aria-hidden />
        <p className="font-display text-sm font-bold">{title}</p>
      </div>
      <p className="text-xs text-fg-muted">{description}</p>
      <div className="mt-auto flex w-full items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-fg-muted">{duration}</span>
        <span className="font-display text-sm font-bold tabular-nums">{formatEuro(cost)}</span>
      </div>
    </button>
  );
}
